/**
 * Hooks for the AI console.
 *
 * Two of them, split along the line that matters: what the user is *allowed*
 * to do (slow, cached, fetched once) and what is happening *right now* (fast,
 * per-token, never cached).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { aiApi } from '../../api/endpoints';
import { streamAi, parseReport, MAX_REPORT_LINES } from './aiStream';

/* ==========================================================================
   Capabilities
   ========================================================================== */

/**
 * What the signed-in user may do with the AI.
 *
 * Module-level cache with a TTL rather than per-component state: the Live View
 * mounts and unmounts this on every patient, and re-asking the server whether
 * a doctor holds a grant that changes about once a quarter is a request per
 * consultation for no information.
 *
 * The TTL exists so an admin's grant lands within a minute without the doctor
 * signing out — the alternative, caching for the session, means "I turned it on
 * but he still can't see it" support calls.
 */
let capabilityCache = null;
const CAPABILITY_TTL_MS = 60_000;

/** Called after anything that could change the answer (an admin's own grant). */
export function invalidateAiCapabilities() {
  capabilityCache = null;
}

export function useAiCapabilities({ enabled = true } = {}) {
  const [state, setState] = useState(() =>
    capabilityCache && Date.now() - capabilityCache.at < CAPABILITY_TTL_MS
      ? { data: capabilityCache.data, loading: false, error: null }
      : { data: null, loading: enabled, error: null },
  );

  const load = useCallback(
    async ({ force = false } = {}) => {
      if (!enabled) return null;

      if (!force && capabilityCache && Date.now() - capabilityCache.at < CAPABILITY_TTL_MS) {
        setState({ data: capabilityCache.data, loading: false, error: null });
        return capabilityCache.data;
      }

      setState((current) => ({ ...current, loading: true }));
      try {
        const data = await aiApi.capabilities();
        capabilityCache = { at: Date.now(), data };
        setState({ data, loading: false, error: null });
        return data;
      } catch (error) {
        // Two different failures wear the same exception, and they need
        // opposite answers.
        //
        // A 404 means this backend has no /api/ai routes at all — it predates
        // the AI upgrade, or a deploy rolled back. There is nothing to fall
        // back TO, so the feature hides itself. Anything else (a timeout, a
        // 5xx, a dropped connection) is transient: losing the AI entirely
        // because one GET blipped would be worse than a missing premium toggle,
        // so those still fail open onto the standard tier.
        //
        // This is not hypothetical — a rolled-back backend behind a
        // freshly-deployed frontend left the button on screen, enabled, 404ing
        // on click.
        const routesMissing = error?.status === 404;
        setState({ data: null, loading: false, error, routesMissing });
        return null;
      }
    },
    [enabled],
  );

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const capabilities = state.data;

  return {
    ...state,
    reload: () => load({ force: true }),
    // `routesMissing` is the only failure that closes the feature; see the
    // catch above for why the rest fail open.
    canUseAi: capabilities ? capabilities.canUseAi : !state.routesMissing,
    canUsePremium: Boolean(capabilities?.canUsePremium),
    engineConfigured: capabilities
      ? capabilities.engineConfigured !== false
      : !state.routesMissing,
    routesMissing: Boolean(state.routesMissing),
    usage: capabilities?.usage || { standard: 0, premium: 0 },
    premiumDailyLimit: capabilities?.premiumDailyLimit ?? 0,
    premiumModelLabel: capabilities?.premiumModelLabel || null,
    standardModelLabel: capabilities?.standardModelLabel || null,
  };
}

/* ==========================================================================
   The analysis run
   ========================================================================== */

const IDLE = {
  status: 'idle', // idle | streaming | done | error
  report: '',
  reasoning: '',
  meta: {},
  ladder: [],
  error: null,
  startedAt: null,
  firstTokenAt: null,
  finishedAt: null,
};

/**
 * Run one medical-file analysis and expose it as it arrives.
 *
 * The whole point is that nothing here waits for completion: `report` grows
 * token by token, `ladder` grows rung by rung, and the component re-renders
 * against whatever exists at that instant.
 */
export function useMedicalFileAnalysis() {
  const [state, setState] = useState(IDLE);
  const abortRef = useRef(null);

  // Cancel any run still in flight when the modal closes, so a doctor who
  // clicks away does not leave a paid generation billing in the background.
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((current) =>
      current.status === 'streaming'
        ? { ...current, status: 'done', finishedAt: Date.now(), stopped: true }
        : current,
    );
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState(IDLE);
  }, []);

  const run = useCallback(async ({ patientId, manualPatientCode, premium, specialty, focus }) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const startedAt = Date.now();
    setState({ ...IDLE, status: 'streaming', startedAt });

    try {
      const result = await streamAi(
        '/ai/analyze-medical-file',
        {
          patientId: patientId ?? undefined,
          manualPatientCode: manualPatientCode ?? undefined,
          premium: Boolean(premium),
          specialty: specialty ?? undefined,
          focus: focus ?? undefined,
          lang: 'ar',
        },
        {
          signal: controller.signal,

          onContent: (_token, full) =>
            setState((current) => ({
              ...current,
              report: full,
              firstTokenAt: current.firstTokenAt ?? Date.now(),
            })),

          onReasoning: (_token, full) =>
            setState((current) => ({ ...current, reasoning: full })),

          /**
           * The fallback ladder, made visible.
           *
           * Each `connecting` frame is a rung. The previous rung is marked
           * failed the moment a new one starts — the server only sends
           * `connecting` for a model when the one before it has already been
           * written off, so the transition itself is the evidence.
           */
          onMeta: (frame, merged) =>
            setState((current) => {
              let ladder = current.ladder;

              if (frame.stage === 'connecting') {
                ladder = [
                  ...current.ladder.map((rung) =>
                    rung.state === 'trying' ? { ...rung, state: 'failed' } : rung,
                  ),
                  { model: frame.model, tier: frame.tier, attempt: frame.attempt, state: 'trying' },
                ];
              } else if (frame.stage === 'done') {
                ladder = current.ladder.map((rung) =>
                  rung.state === 'trying' ? { ...rung, state: 'served' } : rung,
                );
              } else if (frame.stage === 'error' || frame.stage === 'refused') {
                ladder = current.ladder.map((rung) =>
                  rung.state === 'trying' ? { ...rung, state: 'failed' } : rung,
                );
              }

              return { ...current, meta: merged, ladder };
            }),

          onError: (message) => setState((current) => ({ ...current, error: message })),
        },
      );

      // An aborted run is still "done": the doctor stopped it, which is a
      // finished state, not a failure — `stopped` is what distinguishes them
      // in the footer.
      setState((current) => ({
        ...current,
        status: 'done',
        stopped: result.aborted || current.stopped,
        report: result.content || current.report,
        reasoning: result.reasoning || current.reasoning,
        meta: { ...current.meta, ...result.meta },
        finishedAt: Date.now(),
      }));

      return result;
    } catch (error) {
      setState((current) => ({
        ...current,
        status: 'error',
        error: error.message || 'تعذر تشغيل التحليل',
        finishedAt: Date.now(),
      }));
      return null;
    } finally {
      abortRef.current = null;
    }
  }, []);

  /* --- derived ---------------------------------------------------------- */

  // `report` carries the findings AND whether the model honoured the ten-line
  // contract at all. The second part is what stops a reasoning model's chain of
  // thought being rendered as clinical findings — see parseReport().
  const report = useMemo(() => parseReport(state.report), [state.report]);
  const findings = report.findings;

  const timing = useMemo(() => {
    if (!state.startedAt) return { firstTokenMs: null, totalMs: null };
    return {
      firstTokenMs: state.firstTokenAt ? state.firstTokenAt - state.startedAt : null,
      totalMs: (state.finishedAt || Date.now()) - state.startedAt,
    };
  }, [state.startedAt, state.firstTokenAt, state.finishedAt]);

  return {
    ...state,
    findings,
    // Lines the model emitted that are not report lines — its thinking, spoken
    // aloud. Shown in the reasoning drawer, never in the report.
    aside: report.aside,
    followedFormat: report.followedFormat,
    rawReport: report.raw,
    timing,
    // The prompt caps the report at ten lines. Showing the count against the
    // budget is how a prompt regression becomes visible to whoever is looking
    // at the screen, rather than only to whoever reads the test output.
    lineBudget: MAX_REPORT_LINES,
    overBudget: findings.length > MAX_REPORT_LINES,
    isStreaming: state.status === 'streaming',
    run,
    stop,
    reset,
  };
}
